import React from "react";
import { motion } from "framer-motion";
import { Container, Factory, Boxes, CheckCircle2, Zap } from "lucide-react";
import { CONTAINER_PRODUCTS } from "@/data/smartContainer";

const SUMMARY = [
  { label: "Container Fill", value: "82%", icon: Container, accent: "text-blue-600" },
  { label: "Manufacturers", value: "5", icon: Factory, accent: "text-slate-900" },
  { label: "Products", value: "18", icon: Boxes, accent: "text-slate-900" },
];

const buildCells = () => {
  const cells = [];
  CONTAINER_PRODUCTS.forEach((p) => { for (let i = 0; i < p.pallets; i++) cells.push(p); });
  while (cells.length < 21) cells.push(null);
  return cells.slice(0, 21);
};

export default function BuilderVisual() {
  const cells = buildCells();
  return (
    <div data-testid="builder-visual" className="dm-card p-3 sm:p-4 w-full">
      <div className="flex items-center justify-between px-2 pb-3">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        </div>
        <div className="flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span className="text-[11px] font-medium text-slate-500 tracking-tight">SmartContainer™ Builder · Workspace</span>
        </div>
        <div className="w-12" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Container className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
              <span className="text-xs font-semibold text-slate-900">40HQ Container</span>
            </div>
            <span className="text-[11px] font-medium text-slate-400">Pallet allocation</span>
          </div>
          <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-white/70 p-2.5">
            <div className="grid grid-cols-7 gap-1.5">
              {cells.map((cell, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }} transition={{ delay: 0.15 + i * 0.02, duration: 0.3 }}
                  className="aspect-square rounded-[5px] border"
                  style={cell ? { backgroundColor: `${cell.color}1f`, borderColor: `${cell.color}66` }
                    : { backgroundColor: "#fff", borderColor: "#e2e8f0", borderStyle: "dashed" }}
                  title={cell ? cell.name : "Available"} />
              ))}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
            {CONTAINER_PRODUCTS.map((p) => (
              <div key={p.name} className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-[3px] shrink-0" style={{ backgroundColor: p.color }} />
                <span className="text-[11px] text-slate-600 truncate">{p.name}</span>
                <span className="ml-auto text-[11px] font-medium text-slate-400">{p.pallets}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          {SUMMARY.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.08, duration: 0.4 }}
              className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3">
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-slate-50 border border-slate-200">
                <s.icon className="h-4 w-4 text-slate-500" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-[11px] font-medium text-slate-400">{s.label}</div>
                <div className={`text-lg font-bold leading-none ${s.accent}`}>{s.value}</div>
              </div>
            </motion.div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-medium text-slate-400">Container fill</span>
              <span className="text-[11px] font-semibold text-blue-600">82%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <motion.div initial={{ width: 0 }} whileInView={{ width: "82%" }} viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeOut", delay: 0.3 }} className="h-full rounded-full bg-blue-600" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-2.5">
              <div className="flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                <span className="text-[10px] font-medium">Est. Loading</span>
              </div>
              <div className="mt-1 text-xs font-bold text-emerald-800">Ready</div>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-2.5">
              <div className="flex items-center gap-1.5 text-blue-700">
                <Zap className="h-3.5 w-3.5" strokeWidth={2} />
                <span className="text-[10px] font-medium">Factory Direct</span>
              </div>
              <div className="mt-1 text-xs font-bold text-blue-800">Enabled</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
